import { parseAsArrayOf, parseAsStringLiteral, useQueryStates, parseAsTimestamp } from 'nuqs';

import type {
  ExamEventRegistrationStatus,
  ExamEventRegistrationType,
  ExamEventStatus,
} from '../../type';

const EXAM_EVENT_STATUSES = [
  'SCHEDULED',
  'READY_TO_EXAM',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELED',
] as const;

const EXAM_EVENT_STATUSES_WITH_ALL = ['ALL', ...EXAM_EVENT_STATUSES] as const;

const EXAM_EVENT_REGISTRATION_STATUSES = [
  'OPEN_FOR_REGISTRATION',
  'TEMPORARILY_CLOSED',
  'SESSION_FULL',
  'CANCELED',
  'REGISTRATION_CLOSED',
  'SCHEDULED',
] as const;

const EXAM_EVENT_REGISTRATION_TYPES = ['PUBLIC', 'IN_HOUSE'] as const;

interface UseExamEventScheduleSearchParamsOption {
  defaultValues?: {
    startedAt?: Date;
    endedAt?: Date;
  };
}

export default function useExamEventScheduleSearchParams(
  options?: Readonly<UseExamEventScheduleSearchParamsOption>,
) {
  const { defaultValues } = options ?? {};
  const [
    { status, registrationStatus, registrationType, startedAt, endedAt },
    setExamEventScheduleSearchParams,
  ] = useQueryStates({
    registrationStatus: parseAsArrayOf(
      parseAsStringLiteral(EXAM_EVENT_REGISTRATION_STATUSES),
    ).withDefault([]),
    registrationType: parseAsArrayOf(
      parseAsStringLiteral(EXAM_EVENT_REGISTRATION_TYPES),
    ).withDefault([]),
    status: parseAsStringLiteral(EXAM_EVENT_STATUSES_WITH_ALL).withDefault('ALL'),
    startedAt: parseAsTimestamp.withDefault(
      defaultValues?.startedAt ?? new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    ),
    endedAt: parseAsTimestamp.withDefault(
      defaultValues?.endedAt ??
        (() => {
          const date = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
          date.setHours(23, 59, 59, 999);
          return date;
        })(),
    ),
  });

  const changeStatus = (status: ExamEventStatus | null) => {
    setExamEventScheduleSearchParams({ status });
  };

  const changeRegistrationStatus = (registrationStatus: ExamEventRegistrationStatus[] | null) => {
    setExamEventScheduleSearchParams({ registrationStatus });
  };

  const changeRegistrationType = (registrationType: ExamEventRegistrationType[] | null) => {
    setExamEventScheduleSearchParams({ registrationType });
  };

  const changePeriod = (startedAt: Date | null, endedAt: Date | null) => {
    setExamEventScheduleSearchParams({ startedAt, endedAt });
  };

  return [
    { status, registrationStatus, registrationType, startedAt, endedAt },
    {
      changePeriod,
      changeRegistrationStatus,
      changeRegistrationType,
      changeStatus,
    },
  ] as const;
}
